/// <reference path="../../../../core/extensions/string/string.extension.d.ts" />
import { Component, computed, DestroyRef, inject, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import '../../../../core/extensions/string/string.extension';
import { RecipientSelectorComponent, RecipientSelection, RecipientMode } from '../../components/recipient-selector/recipient-selector.component';
import { ComposeStepComponent } from '../../components/compose-step/compose-step.component';
import { EmailAttachmentsComponent } from '../../components/email-attachments/email-attachments.component';
import { ConfirmationModalComponent } from '../../../../parts/confirmation-modal/confirmation-modal.component';
import { InfoModalComponent } from '../../../../parts/info-modal/info-modal.component';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { CommunicationsService } from '../../../../core/services/messaging/communications.service';
import { ModalActions, ModalTypes } from '../../../../core/enums/base.enums';
import { EmailType } from '../../../../core/enums/data.enums';
import { CommunicationsUtilities } from '../../../../core/utilities/communications.utilities';
import { CommunicationsConstants } from '../../../../core/constants/communications.constants';
import { CommunicationsMessagingConstants, GenericMessagingConstants } from '../../../../core/constants/messaging.constants';
import { SendBulkEmailRequest } from '../../../../core/models/data/send-bulk-email.model';
import { AttachmentSelectionState } from '../../../../core/models/ui/email-attachment.model';
import { MemberSelectionState } from '../../components/communications-member-selector/communications-member-selector.component';
import { PropertySelectionState } from '../../components/communications-property-selector/communications-property-selector.component';
import { BulkActionRequest } from '../../../../core/models/service/bulk-action-request.model';
import { MembersFilter } from '../../../../core/filters/members.filter';
import { PropertiesFilter } from '../../../../core/filters/properties.filter';
import { BaseFormComponent } from '../../../../core/base-classes/base-form-component';
import { CanComponentDeactivate } from '../../../../core/routing/can-deactivate.guard';
import { DirtyFormRegistryService } from '../../../../core/services/dirty-form-registry.service';
import { PageTitleComponent } from '../../../../parts/page-title/page-title.component';

@Component({
  selector: 'ms-communications-page',
  standalone: true,
  imports: [
    PageTitleComponent,
    CommonModule,
    FontAwesomeModule,
    RecipientSelectorComponent,
    ComposeStepComponent,
    EmailAttachmentsComponent,
    ConfirmationModalComponent,
    InfoModalComponent,
  ],
  templateUrl: './communications.page.html'
})
export class CommunicationsPage extends BaseFormComponent implements CanComponentDeactivate {

  private userSessionService = inject(UserSessionService);
  private bootstrapService = inject(BootstrapService);
  private communicationsService = inject(CommunicationsService);
  private dirtyFormRegistry = inject(DirtyFormRegistryService);
  private destroyRef = inject(DestroyRef);

  constructor() {
    super();
    this.dirtyFormRegistry.register(this);
    this.destroyRef.onDestroy(() => this.dirtyFormRegistry.unregister(this));
  }

  public ModalTypes = ModalTypes;

  public readonly sendConfirmationModalId = CommunicationsConstants.SEND_CONFIRMATION_MODAL_ID();
  public readonly sendResultModalId = CommunicationsConstants.SEND_RESULT_MODAL_ID();
  public readonly leaveConfirmationModalId = CommunicationsConstants.LEAVE_CONFIRMATION_MODAL_ID();

  public isSending = signal<boolean>(false);

  public recipientSelection = signal<RecipientSelection | null>(null);
  public attachmentSelection = signal<AttachmentSelectionState | null>(null);

  public organizationId = computed<number | undefined>(
    () => this.userSessionService.activeOrganization()?.organizationId
  );

  public hasRecipients = computed<boolean>(
    () => this.recipientSelection()?.isValid ?? false
  );

  public selectedCount = computed<number>(
    () => this.recipientSelection()?.selectedCount ?? 0
  );

  public hasAttachments = computed<boolean>(
    () => (this.attachmentSelection()?.attachments.length ?? 0) > 0
  );

  // A device upload is still committing — block Send so a file can't be left off the blast.
  public isUploadingAttachments = computed<boolean>(
    () => this.attachmentSelection()?.isUploading ?? false
  );

  @ViewChild(RecipientSelectorComponent) private recipientSelector?: RecipientSelectorComponent;
  @ViewChild(ComposeStepComponent) private composeStep?: ComposeStepComponent;
  @ViewChild(EmailAttachmentsComponent) private emailAttachments?: EmailAttachmentsComponent;
  @ViewChild('sendConfirmationModal') private sendConfirmationModal?: ConfirmationModalComponent;
  @ViewChild('sendResultModal') private sendResultModal?: InfoModalComponent;
  @ViewChild('leaveConfirmationModal') private leaveConfirmationModal?: ConfirmationModalComponent;

  public onRecipientSelectionChanged(selection: RecipientSelection): void {
    this.recipientSelection.set(selection);
  }

  public onAttachmentSelectionChanged(state: AttachmentSelectionState): void {
    this.attachmentSelection.set(state);
  }

  //#region Send

  public attemptSend(): void {
    if (!this.hasRecipients() || this.isUploadingAttachments()) {
      return;
    }

    if (this.composeStep && !this.composeStep.isValid) {
      this.composeStep.markAllTouched();
      return;
    }

    this.openSendConfirmation();
  }

  private openSendConfirmation(): void {
    if (!this.sendConfirmationModal) {
      return;
    }

    // Properties fan out to owner + tenant(s) on send, so we count Properties here, not recipients.
    const isProperties = this.recipientSelection()?.mode === RecipientMode.Properties;
    let message = (isProperties
      ? CommunicationsMessagingConstants.SEND_CONFIRMATION_PROPERTIES()
      : CommunicationsMessagingConstants.SEND_CONFIRMATION())
      .replaceValuesWithObject({ selectedCount: this.selectedCount() });

    // Attachments are virus-scanned before delivery, so the blast is queued rather than sent instantly.
    if (this.hasAttachments()) {
      message += `<br/><br/>${CommunicationsMessagingConstants.ATTACHMENTS_SEND_QUEUE_NOTICE()}`;
    }

    this.sendConfirmationModal.modalType.set(ModalTypes.Question);
    this.sendConfirmationModal.message.set(message);
    this.bootstrapService.manageModal(`#${this.sendConfirmationModalId}`, ModalActions.Show);
  }

  public async onSendConfirmed(): Promise<void> {
    const request = this.buildRequest();
    if (!request) {
      return;
    }

    this.isSending.set(true);
    const result = await this.communicationsService.sendBulkEmail(request);
    this.isSending.set(false);

    this.bootstrapService.manageModal(`#${this.sendConfirmationModalId}`, ModalActions.Hide);

    if (result.success && result.data) {
      this.submitSucceeded = true;
      this.sendResultModal?.modalType.set(ModalTypes.Success);
      this.sendResultModal?.message.set(
        CommunicationsMessagingConstants.SEND_SUCCESS().replaceValuesWithObject({
          totalAffected: result.data.totalAffected ?? 0,
          totalSkipped: result.data.totalSkipped ?? 0,
          totalEvaluated: result.data.totalEvaluated ?? 0
        })
      );
    } else {
      this.sendResultModal?.modalType.set(ModalTypes.Failure);
      this.sendResultModal?.message.set(result.message || CommunicationsMessagingConstants.SEND_FAILURE());
    }

    this.bootstrapService.manageModal(`#${this.sendResultModalId}`, ModalActions.Show);
  }

  public onSendCancelled(): void {
    this.bootstrapService.manageModal(`#${this.sendConfirmationModalId}`, ModalActions.Hide);
  }

  public onResultAcknowledged(): void {
    // A failed send keeps the form intact so the user can retry; a successful send starts a clean blast.
    if (this.sendResultModal?.modalType() === ModalTypes.Success) {
      this.submitSucceeded = false;
      this.recipientSelector?.reset();
      this.composeStep?.reset();
      this.emailAttachments?.reset();
    }
  }

  //#endregion Send

  //#region Leave Confirmation

  // No in-page Cancel affordance exists on this page today (unlike other forms), so this only ever
  // fires from router navigation (Back, sidebar, breadcrumb) via canDeactivate().
  private isDirty(): boolean {
    if (this.submitSucceeded) {
      return false;
    }
    return this.hasRecipients() || this.hasAttachments() || (this.composeStep?.composeForm.dirty ?? false);
  }

  public canDeactivate(): boolean | Promise<boolean> {
    return this.awaitLeaveConfirmation(this.isDirty(), () => {
      this.leaveConfirmationModal?.modalType.set(ModalTypes.Question);
      this.leaveConfirmationModal?.message.set(GenericMessagingConstants.GENERIC_ABORT_CONFIRMATION());
      this.bootstrapService.manageModal(`#${this.leaveConfirmationModalId}`, ModalActions.Show);
    });
  }

  public onLeaveConfirmed(): void {
    this.bootstrapService.manageModal(`#${this.leaveConfirmationModalId}`, ModalActions.Hide);
    this.resolvePendingLeave(true);
  }

  public onLeaveCancelled(): void {
    this.bootstrapService.manageModal(`#${this.leaveConfirmationModalId}`, ModalActions.Hide);
    this.resolvePendingLeave(false);
  }

  //#endregion Leave Confirmation

  //#region Request building

  private buildRequest(): SendBulkEmailRequest | null {
    const orgId = this.organizationId();
    const selection = this.recipientSelection();
    if (!orgId || !selection || !this.composeStep) {
      return null;
    }

    const compose = this.composeStep.getValue();

    const baseRequest: SendBulkEmailRequest = {
      organizationId: orgId,
      emailType: EmailType.BasicEmailBlast,
      subject: compose.subject,
      substitutions: CommunicationsUtilities.buildBasicEmailBlastSubstitutions(compose.body, compose.footer)
    };

    if (selection.mode === RecipientMode.Members && selection.memberState) {
      baseRequest.memberRecipients = this.toBulkActionRequest<MembersFilter>(selection.memberState);
    } else if (selection.mode === RecipientMode.Properties && selection.propertyState) {
      baseRequest.propertyRecipients = this.toBulkActionRequest<PropertiesFilter>(selection.propertyState);
    } else {
      return null;
    }

    // Attachments live in the Document Center; the send request carries their ids only.
    const attachmentDocumentIds = (this.attachmentSelection()?.attachments ?? []).map(attachment => attachment.documentId);
    if (attachmentDocumentIds.length > 0) {
      baseRequest.attachmentDocumentIds = attachmentDocumentIds;
    }

    return baseRequest;
  }

  private toBulkActionRequest<T>(state: MemberSelectionState | PropertySelectionState): BulkActionRequest<T> {
    return {
      useFilter: state.useFilter,
      filter: state.filter as T | null,
      selectedIds: state.selectedIds,
      excludedIds: state.excludedIds,
      note: null
    };
  }

  //#endregion Request building
}
